import React, { useEffect } from 'react';
import classnames from 'classnames';
import useMapping from '@gp/hooks/useMapping';
import { DataType } from '@gp/enums/common';
import TinyEditor from '@gp/components/TinyEditor';
import type { IBaseProps } from '@home/types/common';
import type { IRecord } from '@gp/types/global';
import type { IDPSReportUpdatesOfTheWeekChangeType, IDPSReportUpdatesOfTheWeekChangeVO } from '@gp/types/dpsReport/v3';
import type { IDynamicFormControlValueAccessor } from '@gp/components/DynamicForm/components/FormControl/types';

import './index.scss';

interface IUpdatesInputsProps
  extends IBaseProps,
  IDynamicFormControlValueAccessor<IDPSReportUpdatesOfTheWeekChangeVO[]> {
  disabled?: boolean;
  dataType?: DataType;
  projectId?: string;
}

const SPLITTER = '\n';

const PLACEHOLDERS1: Record<IDPSReportUpdatesOfTheWeekChangeType, string> = {
  1: 'Please input Time Changes',
  2: 'Please input Financials Changes',
  3: 'Please input Updates Changes',
  4: 'Please input Status Changes',
};

const PLACEHOLDERS2: Record<IDPSReportUpdatesOfTheWeekChangeType, string> = {
  1: 'No Time Changed',
  2: 'No Financials Changed',
  3: 'No Updates Changed',
  4: 'No Status Changed',
};

const buildKey = (params?: {
  projectId?: string;
  changeType?: IDPSReportUpdatesOfTheWeekChangeType;
  domainId?: string;
}) => {
  const { projectId, changeType, domainId } = params || {};
  return `${projectId}_${changeType}_${domainId}`;
};

const buildChangedMapping = (
  projectId?: string,
  value?: IDPSReportUpdatesOfTheWeekChangeVO[] | null,
  initialChanged = false,
): IRecord<boolean> => {
  if (!Array.isArray(value) || !value.length) return {};
  return value.reduce<IRecord<boolean>>((acc, cur) => {
    const { change_type: changeType, domain_id: domainId } = cur || {};
    const key = buildKey({
      projectId,
      changeType,
      domainId,
    });
    return { ...acc, [key]: initialChanged };
  }, {});
};

const useChangedChecker = (projectId?: string, value?: IDPSReportUpdatesOfTheWeekChangeVO[] | null) => {
  const [changedMapping, { set: setChanged, setAll: setAllChanged }] = useMapping<boolean>({});

  useEffect(() => {
    const changedMappingTmp = buildChangedMapping(projectId, value);
    setAllChanged(changedMappingTmp);
  }, [projectId]);

  return { changedMapping, setChanged };
};

const UpdatesInputs: React.FC<IUpdatesInputsProps> = (props) => {
  const { style, className, disabled = false, dataType, projectId, value, onChange, readOnly = false } = props;

  const { changedMapping, setChanged } = useChangedChecker(projectId, value);

  return (
    <div style={style} className={classnames('dbg-dps-report-update-input-list', className)}>
      {value?.map((valueItem) => {
        const { change_type: changeType, domain_id: domainId, content, status = 'success' } = valueItem || {};
        const key = buildKey({
          projectId,
          changeType,
          domainId,
        });
        const changed = changedMapping?.[key];
        const placeholders = dataType === DataType.AutoFetched && !changed ? PLACEHOLDERS2 : PLACEHOLDERS1;
        const placeholder = placeholders?.[changeType] || '';

        return (
          <TinyEditor
            key={key}
            synchronize
            className={classnames('dbg-common-editor gp-word-break-word dbg-dps-report-update-input-item', {
              'dbg-common-editor--disabled': disabled,
            })}
            placeholder={placeholder}
            minHeight={120}
            status={status}
            readOnly={readOnly || disabled}
            value={content?.join(`${SPLITTER}`)}
            getEditorContent={(payload) => {
              const valueItemIdx = value?.findIndex((item) => item?.change_type === changeType);
              if (valueItemIdx !== -1) {
                const valueItem = value?.[valueItemIdx];
                const valueItemTmp = {
                  ...valueItem,
                  domain_id: '',
                  content: payload?.split(SPLITTER) || [],
                };
                const valueTmp = [...value?.slice(0, valueItemIdx), valueItemTmp, ...value?.slice(valueItemIdx + 1)];
                setChanged(key, true);
                onChange?.(valueTmp);
              }
              // onChange?.(cvm, key, payload);
            }}
            getEditorText={() => {}}
            getIsFocused={() => {}}
          />
        );
      })}
    </div>
  );
};

export default UpdatesInputs;
